import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaBox,
  FaShoppingBag,
  FaUsers,
  FaChartLine,
  FaTags,
  FaHome,
  FaSignOutAlt,
  FaCog,
  FaBell,
} from 'react-icons/fa';
import ProductsManagement from '../components/admin/ProductsManagement';
import OrdersManagement from '../components/admin/OrdersManagement';
import UsersManagement from '../components/admin/UsersManagement';
import AnalyticsDashboard from '../components/admin/AnalyticsDashboard';
import PromotionsManagement from '../components/admin/PromotionsManagement';
import LoadingSpinner from '../components/LoadingSpinner';
import '../styles/AdminDashboard.css';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('analytics');
  const [loading, setLoading] = useState(true);
  const [adminInfo, setAdminInfo] = useState({});
  const [notifications, setNotifications] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/auth');
          return;
        }

        const response = await fetch('/api/user/check-admin', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await response.json();

        if (!data.isAdmin) {
          navigate('/');
          return;
        }

        // Fetch admin info
        const adminResponse = await fetch('/api/admin/info', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const adminData = await adminResponse.json();
        setAdminInfo(adminData);

        // Fetch notifications
        const notificationsResponse = await fetch('/api/admin/notifications', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const notificationsData = await notificationsResponse.json();
        setNotifications(notificationsData);
      } catch (error) {
        // Import logger to handle the error properly
        import('../services/logger').then(({ default: logger }) => {
          logger.error('Error checking admin status', error, { page: 'AdminDashboard' });
        });
        import('../services/errorHandler').then(({ default: errorHandler }) => {
          errorHandler.handleApiError(error);
        });
        navigate('/auth');
      } finally {
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, [navigate]);

  const handleSignOut = () => {
    localStorage.removeItem('token');
    navigate('/auth');
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className='admin-dashboard'>
      <div className='admin-sidebar'>
        <div className='admin-sidebar-header'>
          <h2>Admin Panel</h2>
          <p>{adminInfo.username}</p>
        </div>

        <div className='admin-sidebar-menu'>
          <button
            className={`admin-menu-item ${activeTab === 'analytics' ? 'active' : ''}`}
            onClick={() => setActiveTab('analytics')}
          >
            <FaChartLine /> <span>Analytics</span>
          </button>

          <button
            className={`admin-menu-item ${activeTab === 'products' ? 'active' : ''}`}
            onClick={() => setActiveTab('products')}
          >
            <FaBox /> <span>Products</span>
          </button>

          <button
            className={`admin-menu-item ${activeTab === 'orders' ? 'active' : ''}`}
            onClick={() => setActiveTab('orders')}
          >
            <FaShoppingBag /> <span>Orders</span>
          </button>

          <button
            className={`admin-menu-item ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            <FaUsers /> <span>Users</span>
          </button>

          <button
            className={`admin-menu-item ${activeTab === 'promotions' ? 'active' : ''}`}
            onClick={() => setActiveTab('promotions')}
          >
            <FaTags /> <span>Promotions</span>
          </button>

          <button
            className={`admin-menu-item ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            <FaCog /> <span>Settings</span>
          </button>
        </div>

        <div className='admin-sidebar-footer'>
          <button className='admin-menu-item' onClick={() => navigate('/')}>
            <FaHome /> <span>View Store</span>
          </button>

          <button className='admin-menu-item' onClick={handleSignOut}>
            <FaSignOutAlt /> <span>Sign Out</span>
          </button>
        </div>
      </div>

      <div className='admin-content'>
        <div className='admin-topbar'>
          <h1>
            {activeTab === 'analytics' && 'Dashboard Overview'}
            {activeTab === 'products' && 'Product Management'}
            {activeTab === 'orders' && 'Orders Management'}
            {activeTab === 'users' && 'User Management'}
            {activeTab === 'promotions' && 'Promotions & Discounts'}
            {activeTab === 'settings' && 'Admin Settings'}
          </h1>

          <div className='admin-notifications'>
            <button className='notification-bell'>
              <FaBell />
              {notifications.length > 0 && (
                <span className='notification-badge'>{notifications.length}</span>
              )}
            </button>
          </div>
        </div>

        {activeTab === 'analytics' && <AnalyticsDashboard />}
        {activeTab === 'products' && <ProductsManagement />}
        {activeTab === 'orders' && <OrdersManagement />}
        {activeTab === 'users' && <UsersManagement />}
        {activeTab === 'promotions' && <PromotionsManagement />}
        {activeTab === 'settings' && (
          <div className='admin-settings'>
            <h2>Admin Settings</h2>
            <p>Configure store settings, user permissions, and more.</p>
            {/* Settings content would go here */}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
